
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sun, 
  ArrowUp, 
  Mail, 
  Twitter, 
  Instagram, 
  Layers, 
  ShieldCheck,
  Zap,
  Lock,
  MessageSquare,
  X,
  Info,
  ShieldAlert,
  Fingerprint
} from 'lucide-react';
import { Logo } from './Logo';
import { GlowingEffect } from './GlowingEffect';
import { cn } from '../../lib/utils';

/**
 * Information mapping for all footer links
 */
const INFO_REGISTRY: Record<string, { title: string, desc: string, icon: React.ReactNode }> = {
  "Scarcity Control": {
    title: "Scarcity Control",
    desc: "Dynamic inventory management that prevents over-saturation and maintains the exclusive value of your digital assets by limiting simultaneous access sessions.",
    icon: <Layers className="size-5" />
  },
  "Watermarking": {
    title: "Invisible Watermarking",
    desc: "Proprietary steganography that embeds buyer-specific identifiers into every file. If a file leaks, we know exactly which member shared it.",
    icon: <Fingerprint className="size-5" />
  },
  "Leak Analysis": {
    title: "24/7 Leak Analysis",
    desc: "Advanced AI-driven bots monitor Telegram, Discord, and dark web forums to identify, flag, and neutralize unauthorized distribution of your work.",
    icon: <ShieldAlert className="size-5" />
  },
  "Secure Vault": {
    title: "Secure Vault Infrastructure",
    desc: "Military-grade AES-256 encryption for your source files. Your content never touches the public internet without a valid, time-sensitive decryption key.",
    icon: <Lock className="size-5" />
  },
  "Token Engine": {
    title: "One-Time Token Engine",
    desc: "Zero-trust access tokens that are device-locked and time-sensitive. A link shared with a friend becomes invalid the moment it's opened elsewhere.",
    icon: <Zap className="size-5" />
  },
  "Encryption": {
    title: "End-to-End Encryption",
    desc: "TLS 1.3 encryption for all data transmissions between our secure OS and your members, ensuring privacy and preventing man-in-the-middle attacks.",
    icon: <ShieldCheck className="size-5" />
  },
  "Reseller Block": {
    title: "Reseller Deterrence",
    desc: "Proactive blocking of known reseller IP ranges and automated detection of suspicious bulk purchase patterns commonly used by group-buy sites.",
    icon: <ShieldAlert className="size-5" />
  },
  "Member View": {
    title: "Hifi Member Portal",
    desc: "A fully white-labeled experience that replaces standard Whop links with a custom, premium OS interface that increases your brand's perceived value.",
    icon: <Sun className="size-5" />
  },
  "Auth Portal": {
    title: "Secure Auth Portal",
    desc: "Deep integration with the Whop API, providing a seamless and highly secure bridge between your store and your protected digital fortress.",
    icon: <KeyIcon className="size-5" />
  },
  "Terms of OS": {
    title: "Operational Terms",
    desc: "Our platform's framework designed to protect both the creator and the agency, ensuring fair use and maximum security for all community participants.",
    icon: <Info className="size-5" />
  },
  "Privacy Policy": {
    title: "Privacy First Initiative",
    desc: "We prioritize your members' data. Aure OS utilizes anonymous fingerprinting, meaning we secure assets without storing unnecessary personal information.",
    icon: <ShieldCheck className="size-5" />
  },
  "Data Protection": {
    title: "Data Safeguard Protocols",
    desc: "Redundant server clusters and automated daily backups ensure that your digital legacy is protected against both human error and malicious intent.",
    icon: <Lock className="size-5" />
  }
};

function KeyIcon(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m15.5 7.5 2.3 2.3a1 1 0 0 0 1.4 0l2.1-2.1a1 1 0 0 0 0-1.4L19 4" />
      <path d="m21 2-9.6 9.6" />
      <circle cx="7.5" cy="15.5" r="5.5" />
    </svg>
  );
}

function BackToTop() {
  const [isHovered, setIsHovered] = useState(false);
  
  const handleScrollTop = () => {
    window.scroll({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <div 
      className="flex items-center justify-center"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button 
        type="button" 
        onClick={handleScrollTop}
        className="group relative flex items-center gap-3 px-6 py-3 rounded-full border border-border/40 bg-muted/5 transition-all duration-500 hover:bg-muted/10 hover:border-primary/30"
      >
        <GlowingEffect spread={30} glow={isHovered} disabled={false} proximity={50} inactiveZone={0} borderWidth={1.5} />
        <ArrowUp className="size-3.5 text-muted-foreground transition-transform group-hover:-translate-y-0.5" />
        <span className="text-[9px] font-bold tracking-[0.4em] uppercase text-muted-foreground group-hover:text-foreground transition-colors">
          Return to Hub
        </span>
      </button>
    </div>
  );
}

const navigation = {
  categories: [
    {
      id: "main",
      name: "Navigation",
      sections: [
        {
          id: "protection",
          name: "DRM Engine",
          items: [
            { name: "Scarcity Control" },
            { name: "Watermarking" },
            { name: "Leak Analysis" },
          ],
        },
        {
          id: "infrastructure",
          name: "Infrastructure",
          items: [
            { name: "Secure Vault" },
            { name: "Token Engine" },
            { name: "Encryption" },
          ],
        },
        {
          id: "solutions",
          name: "Whop Solutions",
          items: [
            { name: "Reseller Block" },
            { name: "Member View" },
            { name: "Auth Portal" },
          ],
        },
        {
          id: "legal",
          name: "Legal",
          items: [
            { name: "Terms of OS" },
            { name: "Privacy Policy" },
            { name: "Data Protection" },
          ],
        },
      ],
    },
  ],
};

const SocialLinkStyle = `size-11 flex items-center justify-center border border-dotted border-border/60 rounded-full transition-all duration-300 hover:bg-primary/5 hover:border-primary/40 hover:-translate-y-1 transform-gpu`;

export function Footer() {
  const [activeInfo, setActiveInfo] = useState<{ title: string, desc: string, icon: React.ReactNode } | null>(null);

  return (
    <footer className="w-full relative overflow-hidden bg-background pt-32 pb-20">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/40 to-transparent" />
      
      {/* Info Modal Overlay */}
      <AnimatePresence>
        {activeInfo && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setActiveInfo(null)}
              className="absolute inset-0 bg-background/60 backdrop-blur-xl"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-lg p-1"
            >
              <GlowingEffect spread={100} glow={true} disabled={false} proximity={0} inactiveZone={0} borderWidth={1.5} />
              <div className="relative bg-card/80 backdrop-blur-3xl border border-border/40 rounded-[32px] p-10 shadow-2xl overflow-hidden">
                <button 
                  onClick={() => setActiveInfo(null)}
                  className="absolute top-6 right-6 p-2 rounded-full hover:bg-muted/40 transition-colors text-muted-foreground hover:text-foreground"
                >
                  <X size={18} />
                </button>
                <div className="space-y-6">
                  <div className="size-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                    {activeInfo.icon}
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-2xl font-light tracking-tight">{activeInfo.title}</h3>
                    <p className="text-[10px] font-bold tracking-[0.4em] uppercase text-primary/40">Security Specification</p>
                  </div>
                  <p className="text-muted-foreground font-light leading-relaxed">
                    {activeInfo.desc}
                  </p>
                  <button 
                    onClick={() => setActiveInfo(null)}
                    className="w-full py-4 rounded-2xl bg-foreground text-background text-[10px] font-bold tracking-[0.3em] uppercase hover:scale-[0.98] transition-all"
                  >
                    Got it
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-full pointer-events-none opacity-20">
        <div className="absolute bottom-[-10%] left-[-10%] size-[600px] bg-primary/10 blur-[120px] rounded-full" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Intro Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
          <div className="lg:col-span-3 flex justify-center lg:justify-start">
             <Logo />
          </div>
          <div className="lg:col-span-9">
            <p className="text-center lg:text-left text-xs leading-6 text-muted-foreground/60 font-light max-w-4xl tracking-tight">
              Aure OS represents the pinnacle of digital scarcity for the Whop ecosystem. 
              Our proprietary DRM engine ensures that your community's value remains exclusive to paying members, 
              killing resellers and recovering lost revenue in real-time.
            </p>
          </div>
        </div>

        <div className="border-b border-dotted border-border/40 w-full mb-16" />

        {/* Navigation Grid */}
        <div className="mb-20 flex justify-center">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 md:gap-24 text-center md:text-left">
            {navigation.categories[0].sections.map((section) => (
              <div key={section.id} className="space-y-6">
                <h4 className="text-[10px] font-bold tracking-[0.4em] uppercase text-foreground">
                  {section.name}
                </h4>
                <ul role="list" className="flex flex-col space-y-3">
                  {section.items.map((item) => (
                    <li key={item.name}>
                      <button
                        onClick={() => {
                          const info = INFO_REGISTRY[item.name];
                          if (info) setActiveInfo(info);
                          else setActiveInfo({ title: item.name, desc: "Information about this module is currently restricted. Please contact our support team for documentation.", icon: <Info size={18} /> });
                        }}
                        className="text-[11px] text-muted-foreground hover:text-primary transition-all duration-300 tracking-wider font-light"
                      >
                        {item.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Socials & Utility Row */}
        <div className="flex flex-col lg:flex-row items-center justify-between gap-10 pb-16 border-b border-border/10">
          <div className="flex flex-wrap items-center justify-center gap-4">
            <SocialIcon 
              icon={<Mail size={18} />} 
              href="mailto:aureos.app@gmail.com"
              title="Email Support"
            />
            <SocialIcon 
              icon={<Twitter size={18} />} 
              onClick={() => setActiveInfo({ title: "Twitter Hub", desc: "Follow @AureOS_Secure for system updates and industry news.", icon: <Twitter size={18}/> })} 
            />
            <SocialIcon 
              icon={<Instagram size={18} />} 
              href="https://www.instagram.com/aureos.app/"
              title="Instagram"
            />
            <SocialIcon 
              icon={<MessageSquare size={18} />} 
              onClick={() => setActiveInfo({ title: "Telegram", desc: "Direct engineering support for active community owners.", icon: <MessageSquare size={18}/> })} 
            />
          </div>
          
          <BackToTop />
        </div>

        {/* Bottom Centered Copyright */}
        <div className="pt-16 flex flex-col items-center justify-center text-center">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold tracking-[0.4em] text-muted-foreground/30 uppercase">
              © 2026 — Agency Reserve
            </span>
          </div>
          <p className="mt-4 text-[8px] tracking-[0.6em] text-muted-foreground/10 font-bold uppercase pointer-events-none">
            ENCRYPTED CLUSTER DEPLOYMENT
          </p>
        </div>
      </div>
    </footer>
  );
}

function SocialIcon({ icon, onClick, href, title }: { icon: React.ReactNode; onClick?: () => void; href?: string; title?: string }) {
  const [isHovered, setIsHovered] = React.useState(false);

  const content = (
    <div className="text-muted-foreground group-hover:text-foreground transition-colors relative z-10">
      {icon}
    </div>
  );

  return (
    <div 
      className="relative p-0.5"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title={title}
    >
      <GlowingEffect spread={30} glow={isHovered} disabled={false} proximity={50} inactiveZone={0.01} borderWidth={1.5} />
      {href ? (
        <a
          href={href}
          target={href.startsWith('mailto:') ? undefined : "_blank"}
          rel={href.startsWith('mailto:') ? undefined : "noopener noreferrer"}
          aria-label={title || "Social Link"}
          className={cn(SocialLinkStyle, isHovered && "bg-muted/5")}
        >
          {content}
        </a>
      ) : (
        <button
          aria-label={title || "Social Link"}
          onClick={onClick}
          className={cn(SocialLinkStyle, isHovered && "bg-muted/5")}
        >
          {content}
        </button>
      )}
    </div>
  );
}
